import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Clock, Minus, Plus, Loader2, Lock, Check } from 'lucide-react';
import { toast } from 'sonner';

const STEP = 0.25;
const fmt = (h) => (h == null ? '' : String(Math.round(h * 4) / 4).replace(/\.0+$/, ''));
const clamp = (h) => Math.min(24, Math.max(0, Math.round(h * 4) / 4));

/** Own hours for one job on one day. Today: edit directly. Past: read-only + "Request change". */
export const HoursCard = ({ job, onChanged }) => {
  const { api } = useAuth();
  const [value, setValue] = useState(fmt(job.hours?.hours));
  const [saving, setSaving] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [req, setReq] = useState({ hours: '', reason: '' });

  useEffect(() => { setValue(fmt(job.hours?.hours)); }, [job.hours?.hours]);

  const current = job.hours?.hours;
  const dirty = value !== fmt(current) && value !== '';
  const nudge = (d) => setValue(v => fmt(clamp((parseFloat(v) || 0) + d)));

  const save = async () => {
    const h = parseFloat(value);
    if (!(h > 0)) { toast.error('Enter the hours you worked'); return; }
    setSaving(true);
    try {
      await api.put('/portal/hours', { project_id: job.project.id, date: job.date, hours: clamp(h) });
      toast.success('Hours saved');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save hours'); }
    finally { setSaving(false); }
  };

  const submitRequest = async () => {
    const h = parseFloat(req.hours);
    if (!(h > 0)) { toast.error('Enter the corrected hours'); return; }
    if (req.reason.trim().length < 3) { toast.error('Add a short reason'); return; }
    setSaving(true);
    try {
      await api.post('/portal/hours/change-requests', { project_id: job.project.id, date: job.date, requested_hours: clamp(h), reason: req.reason.trim() });
      toast.success('Change request sent to the office');
      setReqOpen(false); setReq({ hours: '', reason: '' });
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send the request'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="job-hours-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-white text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-[#F5A623]" /> Hours worked {job.is_past && <Lock className="w-3.5 h-3.5 text-gray-500 ml-auto" />}</CardTitle>
      </CardHeader>
      <CardContent>
        {job.is_past || !job.is_today ? (
          <div data-testid="job-hours-readonly">
            <div className="flex items-end gap-2">
              <span className="text-4xl font-black text-white tabular-nums" data-testid="job-hours-value">{current != null ? fmt(current) : '—'}</span>
              <span className="text-gray-400 mb-1.5">{current != null ? 'hours' : 'no hours recorded'}</span>
            </div>
            {job.is_past ? (
              <>
                <p className="text-xs text-gray-500 mt-1">Past days can only be changed by the office.</p>
                {job.pending_request ? (
                  <div className="mt-3 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-sm" data-testid="job-hours-pending">
                    <p className="text-amber-300 font-medium">Change to {fmt(job.pending_request.requested_hours)}h requested</p>
                    <p className="text-gray-400 text-xs mt-0.5">Waiting for the office to review. “{job.pending_request.reason}”</p>
                  </div>
                ) : (
                  <Button variant="outline" onClick={() => { setReq({ hours: fmt(current) || '', reason: '' }); setReqOpen(true); }} className="mt-3 h-11 w-full border-white/20 text-white bg-white/5" data-testid="job-hours-request-btn">
                    Request change
                  </Button>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-500 mt-1">You can enter hours for this job on the day.</p>
            )}
          </div>
        ) : (
          <div data-testid="job-hours-editor">
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => nudge(-STEP)} className="h-12 w-12 border-white/20 bg-white/5 text-white" aria-label="Less" data-testid="job-hours-minus"><Minus className="w-5 h-5" /></Button>
              <Input type="number" inputMode="decimal" step={STEP} min="0" max="24" value={value} onChange={e => setValue(e.target.value)} onBlur={() => value !== '' && setValue(fmt(clamp(parseFloat(value) || 0)))} placeholder="0"
                className="h-12 text-center text-2xl font-bold bg-[#1E2530] border-white/20 text-white" data-testid="job-hours-input" />
              <Button type="button" variant="outline" onClick={() => nudge(STEP)} className="h-12 w-12 border-white/20 bg-white/5 text-white" aria-label="More" data-testid="job-hours-plus"><Plus className="w-5 h-5" /></Button>
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5 text-center">Total hours on this job today · quarter-hour steps</p>
            <Button onClick={save} disabled={saving || !dirty} className="mt-3 w-full h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold disabled:opacity-50" data-testid="job-hours-save">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : current != null && !dirty ? <><Check className="w-4 h-4 mr-1.5" /> Saved {fmt(current)}h</> : current != null ? 'Update hours' : 'Save hours'}
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-[400px]" data-testid="hours-request-dialog">
          <DialogHeader><DialogTitle>Request a change</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-400 -mt-2">{job.project.project_code || job.project.project_ref} · {job.date}. The office will review this.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-400">Corrected hours</label>
              <Input type="number" inputMode="decimal" step={STEP} min="0.25" max="24" value={req.hours} onChange={e => setReq(r => ({ ...r, hours: e.target.value }))} className="h-12 text-lg bg-[#1E2530] border-white/20 text-white mt-1" data-testid="hours-request-hours" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Reason</label>
              <Textarea value={req.reason} onChange={e => setReq(r => ({ ...r, reason: e.target.value }))} placeholder="e.g. Forgot to add the afternoon on the second site" className="bg-[#1E2530] border-white/20 text-white mt-1 min-h-[80px]" data-testid="hours-request-reason" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setReqOpen(false)} className="text-gray-400 hover:text-white">Cancel</Button>
            <Button onClick={submitRequest} disabled={saving} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="hours-request-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send request'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
